import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    if (this.dataset.fragranceLive === 'true') {
      loadLiveFragrances(this);
      return;
    }
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    if (this.dataset.fragranceLive === 'true') return;

    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (result.data?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

// Fragrance matching reads the live Alternative Fragrances collection. No stored
// product list is used, so additions, removals, prices and stock refresh together.
const fragranceFold = (value = '') => value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, ' ').trim().replace(/\s+/g, ' ');
const fragranceAliases = { ysl: 'yves saint laurent', dior: 'christian dior', armani: 'giorgio armani', 'dolce gabbana': 'dolce and gabbana', 'd g': 'dolce and gabbana', jpg: 'jean paul gaultier', 'j p gaultier': 'jean paul gaultier', isseymiyake: 'issey miyake', 'thierry mugler': 'mugler', 'mont blanc': 'montblanc', 'montale paris': 'montale', 'cavalli roberto': 'roberto cavalli', dkny: 'donna karan', 'donna karan dkny': 'donna karan', 'j del pozo': 'halloween', 'jesus del pozo': 'halloween', 'by kilian': 'kilian', 'tiffany and co': 'tiffany', 'tiffany co': 'tiffany' };
const fragranceBrand = (value = '') => {
  const name = fragranceFold(value).replace(/\b(?:parfums?|perfumes?|fragrances?|cosmetics?|beauty)\b/g, '').replace(/\s+/g, ' ').trim();
  return fragranceAliases[name] || name;
};
const fragranceNoteAliases = { lavanta: 'lavender', lavande: 'lavender', vanille: 'vanilla', misk: 'musk', musc: 'musk', tarcin: 'cinnamon', karanfil: 'clove', nane: 'mint', bal: 'honey', hurma: 'date', cedarwood: 'cedar', santal: 'sandalwood', yasemin: 'jasmine', jasmin: 'jasmine', gul: 'rose', amberwood: 'amber', bergamote: 'bergamot', mandarine: 'mandarin', greyfurt: 'grapefruit', paculi: 'patchouli' };
const fragranceNotes = new Set('bergamot mandarin orange lemon lime grapefruit neroli petitgrain lavender rose jasmine tuberose iris violet lily orchid peony magnolia freesia gardenia ylang geranium heliotrope mimosa vanilla tonka benzoin honey caramel chocolate cacao coffee praline almond coconut pistachio amber musk sandalwood cedar patchouli vetiver oud incense myrrh labdanum leather tobacco cinnamon clove pepper ginger cardamom saffron nutmeg anise basil sage rosemary mint juniper oakmoss moss fir pine cypress plum pear apple peach cherry pineapple raspberry strawberry blackcurrant fig date watermelon lychee osmanthus ambrette cashmeran ambroxan akigalawood'.split(' '));
const fragranceNoise = new Set('a and by de del des di d du e el en for from la le les l of pour the un une eau edp edt edc perfume perfumes parfume toilette cologne spray vaporisateur vaporizer vaporiser natural concentree concentrate fragrance fragance water tester refill recharge boxed gift set piece pieces pack sp ns'.split(' '));
const fragranceText = (html = '') => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};
const fragranceGender = (text = '', tags = []) => {
  const label = fragranceFold(tags.join(' ') + ' ' + text);
  if (/\b(?:women|woman|female|femme|lady|ladies|for her|kadin)\b/.test(label)) return 'woman';
  if (/\b(?:men|man|male|homme|for him|erkek)\b/.test(label)) return 'man';
  return 'unisex';
};
const fragranceTokens = (value, brand) => {
  let text = value.replace(/\b\d+(?:[.,]\d+)?\s*(?:ml|cl|l|oz)\b/gi, ' ');
  text = fragranceFold(text).replace(/\bfor (?:woman|women|man|men)\b/g, ' ');
  text = text.replace(/\beau (?:de )?(?:parfum|perfume|toilette|cologne)\b/g, ' ');
  if (fragranceBrand(brand) === 'prada') text = text.replace(/\bparadox\b/g, 'paradoxe');
  const brandWords = new Set(fragranceBrand(brand).split(' '));
  return [...new Set(text.split(' ').filter((word) => word && !fragranceNoise.has(word) && !brandWords.has(word)))];
};
const fragranceParse = (title) => {
  const match = title.match(/\binspired\s+by\s+(.+?)\s+-\s+(.+?)\s*$/i);
  return match ? { name: match[1], brand: match[2] } : null;
};
const fragranceFamilyNotes = {
  fresh: 'bergamot mandarin orange lemon lime grapefruit neroli petitgrain mint juniper',
  floral: 'rose jasmine tuberose iris violet lily orchid peony magnolia freesia gardenia ylang geranium heliotrope mimosa osmanthus',
  woody: 'sandalwood cedar patchouli vetiver oud oakmoss moss fir pine cypress cashmeran akigalawood',
  amber: 'amber benzoin tonka vanilla labdanum myrrh incense',
  gourmand: 'honey caramel chocolate cacao coffee praline almond coconut pistachio vanilla',
  spicy: 'cinnamon clove pepper ginger cardamom saffron nutmeg anise',
  fruity: 'plum pear apple peach cherry pineapple raspberry strawberry blackcurrant fig date watermelon lychee',
  aromatic: 'lavender basil sage rosemary mint juniper',
  leather: 'leather tobacco',
};
const fragranceFamilies = (notes, tags, title) => {
  const families = new Set();
  for (const [family, words] of Object.entries(fragranceFamilyNotes)) {
    if (words.split(' ').some((note) => notes.has(note))) families.add(family);
  }
  for (const tag of tags) {
    const family = tag.replace(/^scent-/, '');
    if (tag.startsWith('scent-') && Object.hasOwn(fragranceFamilyNotes, family)) families.add(family);
    if (tag === 'scent-sweet') families.add('gourmand');
  }
  // A name/tag is only a broad style hint, never a manufactured note pyramid.
  const name = fragranceFold(title);
  if (/\b(?:oud|wood|woody|bois)\b/.test(name)) families.add('woody');
  if (/\b(?:rose|floral|fleur|bloom)\b/.test(name)) families.add('floral');
  if (/\b(?:fresh|citrus|citrusovy)\b/.test(name)) families.add('fresh');
  if (/\b(?:vanilla|vanille|caramel|chocolate|gourmand)\b/.test(name)) families.add('gourmand');
  return families;
};
const fragranceProfile = (product) => {
  const identity = fragranceParse(product.title) || { name: product.title, brand: product.vendor };
  const description = fragranceText(product.body_html || product.description || '');
  const confirmed = product.scent_profile || {};
  const noteTags = (product.tags || []).filter((tag) => tag.startsWith('scent-note:')).map((tag) => tag.slice(11));
  const genderTag = (product.tags || []).find((tag) => tag.startsWith('scent-gender:'))?.slice(13);
  const notes = new Set((Array.isArray(confirmed.notes) && confirmed.notes.length ? confirmed.notes : noteTags.length ? noteTags : fragranceFold(description).split(' ').map((word) => fragranceNoteAliases[word] || word)).filter((word) => fragranceNotes.has(word)));
  return {
    product, identity, brand: fragranceBrand(identity.brand), tokens: fragranceTokens(identity.name, identity.brand),
    identified: Boolean(fragranceParse(product.title) || (fragranceBrand(product.vendor) && fragranceBrand(product.vendor) !== 'similar')),
    gender: confirmed.gender || genderTag || fragranceGender(identity.name + ' ' + description, product.tags || []),
    notes, families: fragranceFamilies(notes, product.tags || [], identity.name),
  };
};
let fragranceCatalogPromise;
async function liveFragranceCatalog() {
  if (!fragranceCatalogPromise) fragranceCatalogPromise = (async () => {
    const root = window.Shopify?.routes?.root || '/';
    const pageSize = 250;
    const first = await fetch(`${root}collections/alternative-fragrances/products.json?limit=${pageSize}&page=1`, { cache: 'no-store' });
    if (!first.ok) throw new Error('Alternative fragrance catalog is unavailable');
    const products = (await first.json()).products;
    if (!Array.isArray(products)) throw new Error('Invalid alternative fragrance catalog');
    // Continue until a short page, rather than hard-coding today's catalog size.
    for (let page = 2; products.length % pageSize === 0 && products.length > 0; page += 4) {
      const pages = await Promise.all([page, page + 1, page + 2, page + 3].map(async (number) => {
        const response = await fetch(`${root}collections/alternative-fragrances/products.json?limit=${pageSize}&page=${number}`, { cache: 'no-store' });
        if (!response.ok) throw new Error('Alternative fragrance catalog page is unavailable');
        const body = await response.json();
        if (!Array.isArray(body.products)) throw new Error('Invalid alternative fragrance catalog page');
        return body.products;
      }));
      for (const rows of pages) products.push(...rows);
      if (pages.some((rows) => rows.length < pageSize)) break;
    }
    return [...new Map(products.map((product) => [product.id, product])).values()]
      .filter((product) => product.variants?.some((variant) => variant.available))
      .map(fragranceProfile);
  })().catch((error) => { fragranceCatalogPromise = undefined; throw error; });
  const pending = fragranceCatalogPromise;
  return pending.finally(() => { if (fragranceCatalogPromise === pending) fragranceCatalogPromise = undefined; });
}
function rankLiveFragrances(sourceProduct, profiles) {
  const source = fragranceProfile(sourceProduct);
  const equalTokens = (a, b) => a.length === b.length && a.every((token) => b.includes(token));
  const compatible = (profile) => source.gender === 'unisex' || profile.gender === 'unisex' || source.gender === profile.gender;
  const anchors = profiles.filter((p) => source.identified && p.identified && p.brand === source.brand && equalTokens(p.tokens, source.tokens) && compatible(p));
  for (const anchor of anchors) for (const note of anchor.notes) source.notes.add(note);
  const frequency = new Map();
  for (const profile of profiles) for (const note of profile.notes) frequency.set(note, (frequency.get(note) || 0) + 1);
  const weight = (note) => 1 + Math.log((1 + profiles.length) / (1 + (frequency.get(note) || 0)));
  const eligible = profiles.filter((profile) => String(profile.product.id) !== String(sourceProduct.id) && compatible(profile));
  const strict = eligible.map((profile) => {
    const exact = anchors.includes(profile);
    const shared = [...source.notes].filter((note) => profile.notes.has(note));
    const union = new Set([...source.notes, ...profile.notes]);
    const scent = shared.length >= 2 ? shared.reduce((sum, note) => sum + weight(note), 0) / [...union].reduce((sum, note) => sum + weight(note), 0) : 0;
    const common = source.tokens.filter((token) => profile.tokens.includes(token));
    const distinctive = common.some((word) => !/^(?:19|20)\d{2}$/.test(word) && !fragranceNotes.has(word) && !['man', 'men', 'woman', 'women', 'homme', 'femme', 'male', 'female', 'unisex'].includes(word));
    const family = source.identified && profile.identified && distinctive && profile.brand === source.brand && source.tokens.length ? common.length / Math.max(source.tokens.length, profile.tokens.length) : 0;
    const score = exact ? 1000 : family >= 0.33 ? 100 + 50 * family + 30 * scent : scent >= 0.2 ? 80 * scent : 0;
    return { profile, score, reason: exact ? 'Inspired by this fragrance' : family >= 0.33 ? 'Related fragrance' : 'Similar scent notes' };
  }).filter((row) => row.score > 0).sort((a, b) => b.score - a.score || a.profile.product.title.localeCompare(b.profile.product.title));
  if (strict.length) return strict;
  // Best-effort discovery is separate from genuine inspiration/note matches.
  // Keep weak catalog hints out of the verified scent metadata.
  const hash = (value) => {
    let result = 2166136261;
    for (const character of value) result = Math.imul(result ^ character.charCodeAt(0), 16777619);
    return result >>> 0;
  };
  const broader = eligible.filter((profile) => profile.identified || profile.notes.size).map((profile) => {
    const shared = [...source.notes].filter((note) => profile.notes.has(note));
    const families = [...source.families].filter((family) => profile.families.has(family));
    const sameBrand = source.identified && profile.identified && source.brand === profile.brand;
    const score = shared.length ? 70 + shared.length + shared.reduce((sum, note) => sum + weight(note), 0) / 20
      : families.length ? 50 + families.length / Math.max(source.families.size, profile.families.size)
      : sameBrand ? 30 : 1;
    const reason = shared.length ? 'Shared scent note — broader suggestion'
      : families.length ? 'Similar fragrance family — broader suggestion'
      : sameBrand ? 'Explore this fragrance house’s inspirations'
      : 'Discovery pick — scent details limited';
    const genderAffinity = source.gender !== 'unisex' && source.gender === profile.gender ? 0.2 : 0;
    const tie = hash(String(sourceProduct.id) + ':' + String(profile.product.id)) / 4294967296;
    return { profile, score: score + genderAffinity + tie / 100, reason };
  }).sort((a, b) => b.score - a.score);
  // Avoid presenting different sizes of the same inspiration as four discoveries.
  const seen = new Set();
  return broader.filter(({ profile }) => {
    const identity = profile.brand + ':' + [...profile.tokens].sort().join(' ');
    if (seen.has(identity)) return false;
    seen.add(identity);
    return true;
  });
}
async function loadLiveFragrances(component) {
  if (component.liveFragrancesLoading) return;
  component.liveFragrancesLoading = true;
  const host = component.querySelector('[data-live-fragrance-results]');
  const sourceNode = component.querySelector('[data-live-fragrance-source]');
  if (!host || !sourceNode) return;
  host.setAttribute('aria-busy', 'true');
  try {
    const source = JSON.parse(sourceNode.textContent);
    const profiles = await liveFragranceCatalog();
    if (!component.isConnected) return;
    const matches = rankLiveFragrances(source, profiles).slice(0, Number(component.dataset.maxProducts) || 4);
    const fragment = document.createDocumentFragment();
    for (const { profile, reason } of matches) {
      const product = profile.product;
      const variant = product.variants.find((item) => item.available);
      const article = document.createElement('article');
      article.className = 'resource-list__item live-fragrance-card';
      const link = document.createElement('a');
      link.href = `${window.Shopify?.routes?.root || '/'}products/${product.handle}`;
      link.className = 'live-fragrance-card__link';
      const image = product.images?.[0];
      if (image) {
        const img = document.createElement('img');
        const url = new URL(image.src, location.origin); url.searchParams.set('width', '500');
        img.src = url.href; img.alt = product.title; img.width = image.width || 500; img.height = image.height || 500; img.loading = 'lazy';
        link.append(img);
      }
      const title = document.createElement('p'); title.className = 'live-fragrance-card__title'; title.textContent = product.title;
      const price = document.createElement('p'); price.className = 'price';
      const moneyFormat = source.money_format;
      if (moneyFormat && moneyFormat.includes('{{')) {
        const comma = moneyFormat.includes('comma_separator');
        const decimals = moneyFormat.includes('no_decimals') ? 0 : 2;
        const amount = new Intl.NumberFormat(comma ? 'de-DE' : 'en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(Number(variant.price));
        price.textContent = fragranceText(moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, amount));
      } else {
        price.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: window.Shopify?.currency?.active || 'EUR' }).format(Number(variant.price));
      }
      const explanation = document.createElement('p'); explanation.className = 'live-fragrance-card__reason';
      explanation.textContent = source.title.toLowerCase().includes('inspired by') && reason === 'Inspired by this fragrance' ? 'Same fragrance inspiration' : reason;
      link.append(title, price, explanation); article.append(link); fragment.append(article);
    }
    if (!matches.length) {
      const text = document.createElement('p'); text.textContent = 'Explore our Alternative Fragrances collection while we add more scent information for this perfume.';
      const link = document.createElement('a'); link.href = `${window.Shopify?.routes?.root || '/'}collections/alternative-fragrances`; link.textContent = 'Explore Alternative Fragrances'; link.className = 'button';
      fragment.append(text, link);
    }
    host.replaceChildren(fragment);
    component.dataset.recommendationsPerformed = 'true';
  } catch (error) {
    // Retain available server-rendered recommendations when a request fails.
    if (!host.querySelector('a[href*="/products/"]')) {
      const link = document.createElement('a'); link.href = `${window.Shopify?.routes?.root || '/'}collections/alternative-fragrances`; link.textContent = 'Explore Alternative Fragrances';
      host.replaceChildren(link);
    }
    console.warn('Live fragrance recommendations:', error.message);
  } finally {
    host.removeAttribute('aria-busy');
    component.liveFragrancesLoading = false;
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}



